import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { getBundlePhoto } from "@/lib/productPhotos";

// Homepage strip that spotlights an admin-picked Compound Stack bundle.
// Empty setting → nothing renders (no broken strip). Admin edits the
// pick from /admin (Featured Bundle section).
export default function FeaturedBundleStrip() {
  const { format: fmtPrice } = useCurrency();

  const { data: setting } = useQuery({
    queryKey: ["site-settings", "featured-bundle"],
    queryFn: async () => (await api.get(`/site-settings/featured-bundle`)).data,
  });
  const featuredSlug = setting?.slug;

  const { data: bundles = [] } = useQuery({
    queryKey: ["bundles"],
    queryFn: async () => (await api.get(`/bundles`)).data,
    enabled: !!featuredSlug,
  });
  const bundle = bundles.find((b) => b.slug === featuredSlug);
  if (!featuredSlug || !bundle || bundle.coming_soon) return null;

  const photo = getBundlePhoto(bundle.slug);
  const savings = bundle.original_price ? bundle.original_price - bundle.price : 0;

  return (
    <section className="border-b border-line" data-testid="featured-bundle-strip">
      <div className="container-pd py-16 lg:py-24">
        <p className="label-mono text-brandblue">↳ Buy the stack, save more</p>
        <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Featured bundle
        </h2>

        <Link
          to={`/bundle/${bundle.slug}`}
          className="mt-8 grid grid-cols-1 overflow-hidden rounded-2xl panel-warm bg-white md:grid-cols-[1fr_1fr]"
          data-testid={`featured-bundle-card-${bundle.slug}`}
        >
          <div className="relative aspect-[3/2] bg-white md:aspect-auto">
            {photo ? (
              <img
                src={photo}
                alt={bundle.name}
                className="absolute inset-0 h-full w-full object-contain object-center"
                draggable={false}
                data-testid="featured-bundle-photo"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-subtle">
                <span className="font-mono text-xs tracking-[0.14em]">BUNDLE</span>
              </div>
            )}
            {bundle.save_percent && (
              <span className="absolute left-4 top-4 rounded-full bg-brandblue px-3 py-1.5 font-mono text-[11px] font-bold tracking-[0.12em] text-white">
                SAVE {bundle.save_percent}%
              </span>
            )}
          </div>

          <div className="flex flex-col justify-center p-8 lg:p-10">
            <p className="label-mono text-navy">{bundle.group || "Bundle"}</p>
            <h3 className="mt-2 font-heading text-2xl font-black leading-tight text-ink sm:text-3xl" data-testid="featured-bundle-name">
              {bundle.name}
            </h3>
            <p className="mt-3 text-sm text-subtle">{bundle.short_description}</p>
            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-heading text-3xl font-black text-ink" data-testid="featured-bundle-price">
                {fmtPrice(bundle.price)}
              </span>
              {bundle.original_price ? (
                <span className="text-sm text-subtle line-through">{fmtPrice(bundle.original_price)}</span>
              ) : null}
              {savings > 0 && (
                <span className="rounded-full bg-brandblue/10 px-2.5 py-0.5 font-mono text-[10px] font-bold tracking-[0.1em] text-brandblue">
                  SAVE {fmtPrice(savings)}
                </span>
              )}
            </div>
            <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-mono text-[11px] font-bold tracking-[0.12em] text-white transition-colors group-hover:bg-brandblue">
              VIEW BUNDLE →
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
}
